import { materialRegistry } from '../materials/registry';
import { classifyWeight } from './clientProfile';
function makeContext(profile) {
    return {
        weightClass: classifyWeight(profile?.pesoKg),
        sleepPosition: profile?.posicaoDormir ?? 'misto',
        conditions: new Set(profile?.condicoes ?? []),
        justificativas: [],
    };
}
// ──────────────────────────────────────────────────────────────
// Núcleo — sustentação
// ──────────────────────────────────────────────────────────────
function chooseCore(ctx) {
    // Hérnia / apneia / biotipo pesado → núcleo firme/ensacado
    const wantsVeryFirm = ctx.weightClass === 'heavy' ||
        ctx.conditions.has('hernia-disco') ||
        ctx.conditions.has('apneia');
    // Má circulação / artrose → HR pela ventilação e conforto progressivo
    const wantsHR = ctx.conditions.has('ma-circulacao') ||
        ctx.conditions.has('artrose') ||
        ctx.conditions.has('dor-ombro-quadril');
    // Casal com preferência por isolamento de movimento ou perfil premium
    const wantsPocketCoil = (ctx.conditions.has('hernia-disco') && ctx.weightClass === 'heavy') ||
        (ctx.conditions.has('apneia') && ctx.conditions.has('lombalgia'));
    let materialId;
    let espessuraMm;
    if (wantsPocketCoil) {
        materialId = 'mat-pocket-coil';
        espessuraMm = 180;
        ctx.justificativas.push('Núcleo de molas ensacadas escolhido pela combinação de condições que demanda suporte por pontos, boa ventilação e baixa transmissão de movimento.');
    }
    else if (wantsVeryFirm) {
        materialId = 'mat-d45-core';
        espessuraMm = ctx.weightClass === 'heavy' ? 170 : 150;
        ctx.justificativas.push('Espuma D45 de alta densidade recomendada pelo biotipo e/ou pela necessidade de alinhamento firme da coluna.');
    }
    else if (wantsHR) {
        materialId = 'mat-hr-core';
        espessuraMm = 140;
        ctx.justificativas.push('Núcleo HR (alta resiliência) escolhido pela ventilação superior e retorno ativo — útil para má circulação e conforto progressivo.');
    }
    else {
        materialId = 'mat-d33-core';
        espessuraMm = ctx.weightClass === 'light' ? 120 : 140;
        ctx.justificativas.push('Espuma D33 oferece suporte firme e confiável para biotipo médio, com excelente relação custo-benefício.');
    }
    return {
        material: materialRegistry.getMaterial(materialId),
        espessuraMm,
    };
}
// ──────────────────────────────────────────────────────────────
// Transição
// ──────────────────────────────────────────────────────────────
function chooseTransition(ctx) {
    const hasPressureNeeds = ctx.conditions.has('escoliose') ||
        ctx.conditions.has('fibromialgia') ||
        ctx.conditions.has('lombalgia') ||
        ctx.conditions.has('dor-ombro-quadril');
    const materialId = ctx.conditions.has('ma-circulacao') || hasPressureNeeds
        ? 'mat-transition-hr'
        : 'mat-transition-d28';
    if (materialId === 'mat-transition-hr') {
        ctx.justificativas.push('Camada de transição HR adiciona resposta progressiva entre o núcleo e o conforto, fundamental para condições de pressão.');
    }
    else {
        ctx.justificativas.push('Camada de transição D28 amortece a interface entre núcleo e conforto, distribuindo o peso.');
    }
    return {
        material: materialRegistry.getMaterial(materialId),
        espessuraMm: hasPressureNeeds ? 30 : 25,
    };
}
// ──────────────────────────────────────────────────────────────
// Conforto (topo)
// ──────────────────────────────────────────────────────────────
function chooseComfort(ctx) {
    const pressureConditions = [
        'fibromialgia',
        'bico-papagaio',
        'artrose',
        'escoliose',
        'dor-cervical',
        'dor-ombro-quadril',
    ];
    const needsPressureRelief = pressureConditions.some((c) => ctx.conditions.has(c));
    const prefersCool = ctx.conditions.has('ma-circulacao');
    const heavy = ctx.weightClass === 'heavy';
    let materialId;
    let espessuraMm;
    if (prefersCool && needsPressureRelief) {
        materialId = 'mat-latex-natural';
        espessuraMm = 50;
        ctx.justificativas.push('Látex natural no topo oferece alívio de pressão com ventilação superior — ideal quando há necessidade de conforto e combate ao calor.');
    }
    else if (needsPressureRelief) {
        materialId = heavy ? 'mat-memory-65' : 'mat-memory-50';
        espessuraMm = heavy ? 45 : 35;
        ctx.justificativas.push(`Memory foam ${heavy ? 'D65' : 'D50'} no topo distribui a pressão dos pontos de contato, aliviando ombros, quadril e áreas sensíveis.`);
    }
    else if (prefersCool) {
        materialId = 'mat-latex-synth';
        espessuraMm = 40;
        ctx.justificativas.push('Látex sintético entrega retorno elástico e boa ventilação — bom conforto sem reter calor.');
    }
    else {
        materialId = 'mat-soft-d23';
        espessuraMm = 25;
        ctx.justificativas.push('Espuma macia D23 no topo para toque acolhedor, mantendo custo-benefício.');
    }
    return {
        material: materialRegistry.getMaterial(materialId),
        espessuraMm,
        role: 'top-comfort',
    };
}
// ──────────────────────────────────────────────────────────────
// Acabamento e tecido
// ──────────────────────────────────────────────────────────────
function chooseFinish(ctx) {
    const needsSoftTop = ctx.conditions.has('fibromialgia') ||
        ctx.conditions.has('dor-ombro-quadril') ||
        ctx.sleepPosition === 'lateral';
    if (needsSoftTop) {
        ctx.justificativas.push('Tampo quiltado com malha stretch macia — maximiza acolhimento no contato da pele.');
        return {
            tampo: 'quilted',
            tecidoSuperiorId: 'fab-knit-soft',
            tecidoLateralId: 'fab-suede-side',
        };
    }
    return {
        tampo: 'quilted',
        tecidoSuperiorId: 'fab-quilted-top',
        tecidoLateralId: 'fab-suede-side',
    };
}
// ──────────────────────────────────────────────────────────────
// Combinação final
// ──────────────────────────────────────────────────────────────
export function recommendComposition(profile) {
    const ctx = makeContext(profile);
    // Pré-notas baseadas em posição de dormir
    if (ctx.sleepPosition === 'lateral') {
        ctx.justificativas.push('Dormir de lado demanda alívio de pressão em ombros e quadril — privilegiamos topo com conformidade.');
    }
    else if (ctx.sleepPosition === 'prono') {
        ctx.justificativas.push('Dormir de bruços exige suporte firme para evitar hiperextensão lombar — reduzimos a espessura de conforto macio.');
    }
    else if (ctx.sleepPosition === 'supino') {
        ctx.justificativas.push('Dormir de costas se beneficia de firmeza média com leve conformidade na lombar.');
    }
    const core = chooseCore(ctx);
    const transition = chooseTransition(ctx);
    const comfort = chooseComfort(ctx);
    const finish = chooseFinish(ctx);
    // Correção pra quem dorme de bruços — reduz top comfort pra não afundar
    if (ctx.sleepPosition === 'prono' && comfort.espessuraMm > 30) {
        comfort.espessuraMm = 25;
    }
    const camadas = [
        { materialId: core.material.id, role: 'core', espessuraMm: core.espessuraMm },
        ...(transition
            ? [
                {
                    materialId: transition.material.id,
                    role: 'transition',
                    espessuraMm: transition.espessuraMm,
                },
            ]
            : []),
        { materialId: comfort.material.id, role: comfort.role, espessuraMm: comfort.espessuraMm },
    ];
    // Firmeza percebida simplificada
    const firmezaPercebida = inferPerceivedFirmness(core.material, comfort.material);
    return {
        camadas,
        tampo: finish.tampo,
        tecidoSuperiorId: finish.tecidoSuperiorId,
        tecidoLateralId: finish.tecidoLateralId,
        justificativas: ctx.justificativas,
        firmezaPercebida,
    };
}
function inferPerceivedFirmness(core, comfort) {
    const coreWeight = 0.6;
    const comfortWeight = 0.4;
    const scale = {
        'extra-soft': 1,
        soft: 2,
        'medium-soft': 3,
        medium: 4,
        'medium-firm': 5,
        firm: 6,
        'extra-firm': 7,
    };
    const score = scale[core.firmeza] * coreWeight + scale[comfort.firmeza] * comfortWeight;
    if (score < 2.5)
        return 'macio';
    if (score < 3.5)
        return 'médio-macio';
    if (score < 4.5)
        return 'médio';
    if (score < 5.5)
        return 'médio-firme';
    return 'firme';
}
